import React, { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../contexts/AuthContext';
import { auth, db } from '../config/firebase';
import { ref, get, set } from 'firebase/database';
import MoodboardBlock from './MoodboardBlock';
import MoodboardToolkit from './MoodboardToolkit';
import './MoodboardCanvas.css';

// API URL - we'll use the same backend as your moodboard app
const API_URL = process.env.REACT_APP_API_URL || 'https://moodboards-b8265.web.app';

interface TextParagraph {
  children: { text: string }[];
  type: string;
}

interface Block {
  uuidkey: string;
  contentType: 'text' | 'gif' | 'img';
  dataUrl?: string;
  left: number;
  top: number;
  width: number;
  height: number;
  rotateAngle: number;
  textContent?: TextParagraph[];
}

interface BlockMap {
  [key: string]: Block;
}

interface MoodboardCanvasProps {
  onOpenLoginSignup?: () => void;
  onOpenPhotoBooth?: () => void;
}

const MoodboardCanvas: React.FC<MoodboardCanvasProps> = ({ onOpenLoginSignup, onOpenPhotoBooth }) => {
  const { currentUser } = useAuth();
  const [blockMap, setBlockMap] = useState<BlockMap>({});
  const [pageId, setPageId] = useState<string | null>(null);
  const [focusedBlockKey, setFocusedBlockKey] = useState<string | null>(null);
  const [nextLeft, setNextLeft] = useState(50);
  const [nextTop, setNextTop] = useState(50);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userHandle, setUserHandle] = useState<string | null>(null);
  const [guestBlockMap, setGuestBlockMap] = useState<BlockMap | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Get user handle from handleStore with retry logic for new signups
  const getUserHandle = useCallback(async (userId: string, retryCount = 0): Promise<string | null> => {
    try {
      // First check if we already have the handle
      if (userHandle) return userHandle;

      // Query handleStore to find the handle for this userId
      const handleStoreRef = ref(db, 'handleStore');
      const snapshot = await get(handleStoreRef);

      if (snapshot.exists()) {
        const handleStore = snapshot.val();
        // Find the handle that maps to our userId
        for (const [handle, mappedUserId] of Object.entries(handleStore)) {
          if (mappedUserId === userId) {
            setUserHandle(handle);
            console.log('Garden App - Found user handle:', handle);
            return handle;
          }
        }
      }

      // If no handle found and we haven't retried too many times, wait and retry
      if (retryCount < 5) {
        console.log(`Garden App - No handle found for user: ${userId}, retrying in 2s (attempt ${retryCount + 1}/5)`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return getUserHandle(userId, retryCount + 1);
      }

      console.log('Garden App - No handle found for user after retries:', userId);
      return null;
    } catch (error) {
      console.error('Error getting user handle:', error);
      return null;
    }
  }, [userHandle]);

  // Auto-create or load user's page (or set up guest session)
  useEffect(() => {
    if (currentUser) {
      initializePage();
    } else {
      // Guest mode - use temporary pageId
      setPageId('temp-guest-session');
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for user login during guest session
  useEffect(() => {
    if (currentUser && pageId === 'temp-guest-session') {
      // User just logged in while in guest mode
      // Save their guest work and pass it directly to avoid state timing issues
      console.log('Garden App - User logged in during guest session, preserving work...');
      const guestWork = blockMap;
      setGuestBlockMap(guestWork);
      // Re-initialize to create their real page with guest work
      initializePage(guestWork);
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const initializePage = async (preservedGuestWork?: BlockMap) => {
    if (!currentUser) return;

    try {
      // Get user handle first (with retry logic for new signups)
      const handle = await getUserHandle(currentUser.uid);
      if (!handle) {
        console.error('Garden App - Cannot create page without user handle');
        setLoading(false);
        return;
      }

      // Use handle-based page ID (Firebase-compatible, no dots)
      const userPageId = `inorbit-online_${handle}`;
      console.log('Garden App - Page ID:', userPageId);
      setPageId(userPageId);

      // Try to fetch existing page
      const response = await fetch(`${API_URL}/getPage/${userPageId}`);

      let pageData = null;
      try {
        const responseText = await response.text();
        console.log('Garden App - Get page response:', responseText);

        if (response.ok && responseText && !responseText.startsWith('<!DOCTYPE')) {
          pageData = JSON.parse(responseText);
        }
      } catch (error) {
        console.log('Garden App - Failed to parse page response:', error);
      }

      if (pageData && pageData.blockMap) {
        setBlockMap(pageData.blockMap);
      } else {
        // Page doesn't exist, create it
        // If we have guest work, use that as initial content
        const initialContent = preservedGuestWork || guestBlockMap || {};
        console.log('Garden App - Creating page with initial content:', initialContent);
        await createPage(userPageId, initialContent);
      }
    } catch (error) {
      console.error('Error initializing page:', error);
      // If there's an error, create a new page
      if (currentUser) {
        const handle = await getUserHandle(currentUser.uid);
        if (handle) {
          const userPageId = `inorbit-online_${handle}`;
          await createPage(userPageId);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Store page reference in userCollection
  const storePageInUserCollection = useCallback(async (userId: string, pageId: string) => {
    try {
      const userPageRef = ref(db, `userCollection/${userId}/pages/${pageId}`);
      await set(userPageRef, {
        name: 'inorbit-online',
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString()
      });
      console.log('Garden App - Stored page reference in user collection');
    } catch (error) {
      console.error('Garden App - Error storing page in user collection:', error);
    }
  }, []);

  const createPage = useCallback(async (userPageId: string, initialBlockMap: BlockMap = {}) => {
    if (!currentUser) return;

    try {
      console.log('Garden App - Creating page with blockMap:', initialBlockMap);

      const response = await fetch(`${API_URL}/createPage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pageId: userPageId,
          name: 'inorbit-online',
          userId: currentUser.uid,
          blockMap: initialBlockMap,
          auth: auth
        })
      });

      const responseText = await response.text();
      console.log('Garden App - Create page response:', responseText);

      if (response.ok && responseText && !responseText.startsWith('<!DOCTYPE')) {
        console.log('Garden App - Created new garden page');
        setPageId(userPageId);
        // Set the blockMap to the initial content (guest work or empty)
        setBlockMap(initialBlockMap);

        // Store page reference in user collection
        await storePageInUserCollection(currentUser.uid, userPageId);
      } else {
        console.error('Garden App - Failed to create page:', response.status, responseText);

        // If backend fails but we can still create the local reference
        if (responseText.includes('Internal Server Error')) {
          console.log('Garden App - Backend error, but storing page reference locally');
          setPageId(userPageId);
          setBlockMap(initialBlockMap);
          await storePageInUserCollection(currentUser.uid, userPageId);
        }
      }
    } catch (error) {
      console.error('Garden App - Error creating page:', error);
    }
  }, [currentUser, storePageInUserCollection]);

  // Auto-save functionality (skip for guests)
  const savePage = useCallback(async (updatedBlockMap: BlockMap) => {
    if (!currentUser || !pageId || saving || pageId === 'temp-guest-session') return;

    setSaving(true);
    try {
      console.log('Garden App - Saving blockMap:', updatedBlockMap);
      const response = await fetch(`${API_URL}/savePage/${pageId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'inorbit-online',
          blockMap: updatedBlockMap,
          userId: currentUser.uid,
          auth: auth
        })
      });

      const responseText = await response.text();
      console.log('Garden App - Save page response:', responseText);

      if (response.ok && responseText && !responseText.startsWith('<!DOCTYPE')) {
        console.log('Garden App - Page auto-saved successfully');
      } else {
        console.error('Garden App - Failed to save page:', response.status, responseText);
        // If save fails, try creating the page first
        if (response.status === 500 || responseText.includes('Internal Server Error')) {
          console.log('Garden App - Server error, trying to create page first...');
          await createPage(pageId);
        }
      }
    } catch (error) {
      console.error('Error saving page:', error);
    } finally {
      setSaving(false);
    }
  }, [currentUser, pageId, saving, createPage]);

  // Auto-save when blockMap changes (only if user made changes)
  useEffect(() => {
    if (pageId && hasUnsavedChanges && Object.keys(blockMap).length > 0) {
      const timeoutId = setTimeout(() => {
        savePage(blockMap);
        setHasUnsavedChanges(false); // Reset after save
      }, 2000); // Save 2 seconds after last change

      return () => clearTimeout(timeoutId);
    }
  }, [blockMap, pageId, hasUnsavedChanges, savePage]);

  const addBlock = (type: 'text' | 'gif' | 'img', dataUrl?: string) => {
    const id = uuidv4();

    let newBlock: Block = {
      uuidkey: id,
      contentType: type,
      dataUrl: dataUrl || '',
      left: nextLeft,
      top: nextTop,
      width: type === 'text' ? 200 : 150,
      height: type === 'text' ? 80 : 150,
      rotateAngle: 0
    };

    if (type === 'text') {
      newBlock.textContent = [
        {
          children: [{ text: 'Edit me!' }],
          type: 'paragraph'
        }
      ];
    }

    const updatedBlockMap = {
      ...blockMap,
      [id]: newBlock
    };

    setBlockMap(updatedBlockMap);
    setHasUnsavedChanges(true); // Mark as having unsaved changes
    setNextLeft(nextLeft + 20);
    setNextTop(nextTop + 20);
    setFocusedBlockKey(id);
  };

  const updateBlock = (uuid: string, updates: Partial<Block>) => {
    const updatedBlockMap = {
      ...blockMap,
      [uuid]: {
        ...blockMap[uuid],
        ...updates
      }
    };
    setBlockMap(updatedBlockMap);
    setHasUnsavedChanges(true); // Mark as having unsaved changes
  };

  const deleteBlock = (uuid: string) => {
    const updatedBlockMap = { ...blockMap };
    delete updatedBlockMap[uuid];
    setBlockMap(updatedBlockMap);
    setHasUnsavedChanges(true); // Mark as having unsaved changes
    if (focusedBlockKey === uuid) {
      setFocusedBlockKey(null);
    }
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    // If clicking on canvas (not a block), remove focus
    if (e.target === e.currentTarget) {
      setFocusedBlockKey(null);
    }
  };

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Delete focused block
      if ((e.key === 'Delete' || e.key === 'Backspace') && focusedBlockKey) {
        deleteBlock(focusedBlockKey);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [focusedBlockKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="moodboard-canvas">
        <div className="loading">
          {!userHandle ? 'Setting up your account...' : 'Loading your garden...'}
        </div>
      </div>
    );
  }

  const renderBlocks = Object.keys(blockMap).map(key => {
    const block = blockMap[key];
    return (
      <MoodboardBlock
        key={block.uuidkey}
        block={block}
        isFocused={focusedBlockKey === block.uuidkey}
        onUpdate={(updates) => updateBlock(block.uuidkey, updates)}
        onFocus={() => setFocusedBlockKey(block.uuidkey)}
      />
    );
  });

  return (
    <div className="moodboard-container">
      <div
        className="moodboard-canvas"
        onClick={handleCanvasClick}
      >
        {renderBlocks}
        {saving && <div className="saving-indicator">Saving...</div>}
      </div>
      <MoodboardToolkit
        onAddBlock={addBlock}
        isLoggedIn={!!currentUser}
        onOpenLoginSignup={onOpenLoginSignup}
        onOpenPhotoBooth={onOpenPhotoBooth}
        isSaving={saving}
        hasUnsavedChanges={hasUnsavedChanges}
      />
    </div>
  );
};

export default MoodboardCanvas;